import React, { useState, useCallback } from 'react';
import { CodeBracketSquareIcon, LinkIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';

const ALLOWED_IFRAME_HOSTS = [
  'embed.app.guidde.com',
  'www.youtube.com',
  'youtube.com',
  'player.vimeo.com',
  'docs.google.com',
  'forms.google.com',
  'calendar.google.com',
  'www.google.com',
  'airtable.com',
  'www.figma.com',
  'codepen.io',
  'codesandbox.io',
  'stackblitz.com',
  'loom.com',
  'www.loom.com',
  'typeform.com',
  'tally.so',
  'notion.so',
  'miro.com',
  'whimsical.com',
  'canva.com',
  'pitch.com',
  'slides.com',
  'prezi.com',
  'calendly.com',
  'hubspot.com',
  'jotform.com',
  'surveymonkey.com',
  'open.spotify.com',
  'w.soundcloud.com',
  'soundcloud.com',
  'bandcamp.com',
  'anchor.fm',
  'podcasters.spotify.com',
  'embed.podcasts.apple.com',
  'maps.google.com',
  'www.openstreetmap.org',
  'datastudio.google.com',
  'lookerstudio.google.com',
  'app.powerbi.com',
  'public.tableau.com',
  'e.infogram.com',
  'infogram.com',
  'datawrapper.dwcdn.net',
  'flo.uri.sh',
  'observablehq.com',
];

function parseIframeHtml(html) {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  const iframe = temp.querySelector('iframe');
  if (!iframe) return null;

  return {
    src: iframe.getAttribute('src') || '',
    title: iframe.getAttribute('title') || '',
    width: iframe.getAttribute('width') || '',
    height: iframe.getAttribute('height') || '',
    sandbox: iframe.getAttribute('sandbox') || '',
    allow: iframe.getAttribute('allow') || '',
    allowFullscreen: iframe.hasAttribute('allowfullscreen'),
    style: iframe.getAttribute('style') || '',
  };
}

function isAllowedSrc(src) {
  if (!src) return false;
  try {
    const url = new URL(src);
    return ALLOWED_IFRAME_HOSTS.some(host => url.hostname === host || url.hostname.endsWith('.' + host));
  } catch {
    return false;
  }
}

const EmbedBlock = ({ block, isActive, onChange }) => {
  const [htmlInput, setHtmlInput] = useState('');
  const [error, setError] = useState('');
  const { src = '', title = '', width = '', height = '', sandbox = '', allow = '', allowFullscreen = true, caption = '' } = block.content || {};

  const handlePasteEmbed = useCallback(() => {
    setError('');
    const parsed = parseIframeHtml(htmlInput);

    if (!parsed) {
      setError('No <iframe> found in the pasted HTML. Please paste an embed code containing an iframe.');
      return;
    }

    if (!parsed.src) {
      setError('The iframe is missing a src attribute.');
      return;
    }

    if (!isAllowedSrc(parsed.src)) {
      const hostname = (() => { try { return new URL(parsed.src).hostname; } catch { return parsed.src; } })();
      setError(`"${hostname}" is not in the allowed embed sources. Contact support to add it.`);
      return;
    }

    onChange({
      src: parsed.src,
      title: parsed.title,
      width: parsed.width,
      height: parsed.height,
      sandbox: parsed.sandbox,
      allow: parsed.allow,
      allowFullscreen: parsed.allowFullscreen,
      style: parsed.style,
      caption: '',
    });
    setHtmlInput('');
  }, [htmlInput, onChange]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handlePasteEmbed();
    }
  };

  const computedHeight = (() => {
    if (height && height.includes('px')) return height;
    if (height && !isNaN(parseInt(height))) return `${height}px`;
    return '500px';
  })();

  const computedWidth = (() => {
    if (width && width.includes('px')) return width;
    if (width && width.includes('%')) return width;
    if (width && !isNaN(parseInt(width))) return `${width}px`;
    return '100%';
  })();

  if (!src) {
    return (
      <div className="embed-block border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <CodeBracketSquareIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <p className="text-gray-500 mb-4">Paste an embed code (iframe HTML)</p>

        <div className="max-w-lg mx-auto">
          <textarea
            value={htmlInput}
            onChange={(e) => { setHtmlInput(e.target.value); setError(''); }}
            onKeyDown={handleKeyDown}
            placeholder={'Paste embed HTML here, e.g.:\n<iframe src="https://..." ...></iframe>'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm resize-y"
            rows={4}
          />
          {error && (
            <p className="mt-2 text-sm text-red-600">{error}</p>
          )}
          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-gray-400">
              Supports Guidde, Google Forms, Loom, Calendly, Typeform, and many more
            </p>
            <button
              onClick={handlePasteEmbed}
              disabled={!htmlInput.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <ClipboardDocumentIcon className="w-4 h-4" />
              Embed
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="embed-block flex flex-col items-center">
      <div
        className="relative bg-gray-50 rounded-lg overflow-hidden border border-gray-200"
        style={{ height: computedHeight, width: computedWidth, maxWidth: '100%' }}
      >
        <iframe
          src={src}
          title={title || 'Embedded content'}
          className="w-full h-full"
          frameBorder="0"
          {...(sandbox ? { sandbox } : {})}
          {...(allow ? { allow } : {})}
          {...(allowFullscreen ? { allowFullScreen: true } : {})}
          style={{ borderRadius: '8px' }}
        />
      </div>

      {/* Embed controls */}
      <div className="mt-3 space-y-2 w-full" style={{ maxWidth: computedWidth !== '100%' ? computedWidth : undefined }}>
        {title && (
          <p className="text-sm text-gray-500 truncate" title={title}>
            {title}
          </p>
        )}
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <LinkIcon className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{src}</span>
        </div>

        {/* Caption input */}
        <input
          type="text"
          value={caption}
          onChange={(e) => onChange({ caption: e.target.value })}
          placeholder="Add a caption (optional)"
          className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />

        <div className="flex items-center gap-3">
          <button
            onClick={() => onChange({ src: '', title: '', width: '', height: '', sandbox: '', allow: '', allowFullscreen: false, style: '', caption: '' })}
            className="text-sm text-red-600 hover:text-red-700"
          >
            Remove embed
          </button>
        </div>
      </div>
    </div>
  );
};

export { parseIframeHtml, isAllowedSrc, ALLOWED_IFRAME_HOSTS };
export default EmbedBlock;
